import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useTenant } from '../context/TenantContext';
import { useWebBookingBranding } from '../context/WebBookingBrandingContext';
import { resolveWebPublicExperienceForLocale } from '../types/webPublicExperience';

type BookingFlowHeaderProps = {
  /** Título del flujo (ej. Reservar / Gym). Si es null, no se muestra bloque de título (el H1 va en el contenido). */
  pageTitle: ReactNode | null;
  actions: ReactNode;
  /** Sin logo, muestra un círculo con la inicial del negocio (diseño de referencia de gym). */
  initialFallback?: boolean;
  /**
   * Cabecera contextual de un paso intermedio (diseño de referencia): en los pasos 2 y 3 la barra
   * deja de identificar al negocio — eso ya se sabe — y pasa a decir dónde estás y de qué hablamos
   * ("‹ Sesión de fisioterapia · 45 min · 40 €", "‹ Tu reserva · Pago seguro"). Sustituye a la marca
   * y a las acciones de cuenta/idioma, que vuelven al salir del paso.
   */
  contextBar?: {
    title: string;
    meta?: ReactNode;
    onBack?: () => void;
    backLabel?: string;
  } | null;
};

/**
 * Cabecera unificada booking/gym: logo + nombre de tenant (API o subdominio) y acciones.
 */
/**
 * Publica el alto real de esta cabecera en --wp-app-header-h.
 *
 * Es pegajosa en top:0. Las barras de «Mis reservas» también lo eran, y al ganar por z-index se
 * montaban encima tapando la marca y las acciones de cuenta/idioma — muy visible en los
 * navegadores embebidos de WhatsApp y Telegram, donde no hay barra del navegador que dé pista de
 * dónde empieza la página. Con el alto publicado, esas barras se pegan justo debajo en vez de
 * encima. Se limpia al desmontar para que las pantallas sin esta cabecera (p. ej. /bookings
 * suelto) no arrastren un hueco fantasma.
 */
function usePublishedHeaderHeight() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    if (!el || !root) return;

    const publish = () => {
      root.style.setProperty('--wp-app-header-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    };
    publish();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty('--wp-app-header-h');
    };
  }, []);
  return ref;
}

export function BookingFlowHeader({ pageTitle, actions, initialFallback, contextBar }: BookingFlowHeaderProps) {
  const { t, i18n } = useTranslation();
  const headerRef = usePublishedHeaderHeight();
  const { tenantName: hostTenantName } = useTenant();
  const { tenantName: apiTenantName, logoUrl, webPublicExperience, profileSlug } =
    useWebBookingBranding();

  const displayName = (apiTenantName || hostTenantName || '').trim() || null;
  const exp = resolveWebPublicExperienceForLocale(
    webPublicExperience,
    i18n.language,
  );
  const tagline = exp.branding.tagline?.trim() || null;

  const brandMark = logoUrl ? (
    <img
      className="booking-header-logo"
      src={logoUrl}
      alt=""
      width={44}
      height={44}
      loading="lazy"
      decoding="async"
    />
  ) : initialFallback && displayName ? (
    <span className="booking-header-initial" aria-hidden="true">
      {displayName.trim().charAt(0).toUpperCase()}
    </span>
  ) : null;

  if (contextBar) {
    return (
      <header className="booking-header booking-header--context" ref={headerRef}>
        <div className="booking-header-toolbar">
          {contextBar.onBack ? (
            <button
              type="button"
              className="booking-header-context-back"
              onClick={contextBar.onBack}
              aria-label={contextBar.backLabel ?? t('booking.back')}
            >
              <ChevronLeft size={16} strokeWidth={2.4} aria-hidden />
            </button>
          ) : null}
          <span className="booking-header-context-title">{contextBar.title}</span>
          {contextBar.meta ? <span className="booking-header-context-meta">{contextBar.meta}</span> : null}
        </div>
      </header>
    );
  }

  return (
    <header className="booking-header" ref={headerRef}>
      <div className="booking-header-toolbar">
        <div className="booking-header-brand">
          {profileSlug ? (
            <a href={`/${profileSlug}`} className="booking-header-brand-link" aria-label={t('booking.back_to_profile_aria', { name: displayName ?? '' })}>
              {brandMark}
              <div className="booking-header-brand-text">
                {displayName ? <span className="booking-header-tenant">{displayName}</span> : null}
              </div>
            </a>
          ) : (
            <>
              {brandMark}
              <div className="booking-header-brand-text">
                {displayName ? <span className="booking-header-tenant">{displayName}</span> : null}
              </div>
            </>
          )}
        </div>
        <div className="booking-header-actions">{actions}</div>
      </div>
      {pageTitle != null && pageTitle !== '' ? (
        <div className="booking-header-title-block">
          {tagline ? (
            <p className="booking-header-tagline booking-header-tagline--below">{tagline}</p>
          ) : null}
          <h1 className="booking-header-page-title">{pageTitle}</h1>
        </div>
      ) : tagline ? (
        <div className="booking-header-title-block">
          <p className="booking-header-tagline booking-header-tagline--below">{tagline}</p>
        </div>
      ) : null}
    </header>
  );
}
